import { Component, ContentChild, ViewContainerRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayModule, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DropdownPanelDirective, DropdownTriggerDirective } from './overlay-dropdown.directive';

export enum OverlayDropdownPosition {
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',

  TOP_START = 'top-start',
  TOP_END = 'top-end',

  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',

  LEFT_START = 'left-start',
  LEFT_END = 'left-end',
}

@Component({
  selector: 'app-overlay-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    OverlayModule,
    PortalModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  template: `
    <ng-content></ng-content>
  `,
})
export class OverlayDropdownComponent {
  @Input() position: OverlayDropdownPosition[] = [OverlayDropdownPosition.BOTTOM_START];

  @ContentChild(DropdownTriggerDirective)
  triggerDir!: DropdownTriggerDirective;

  @ContentChild(DropdownPanelDirective)
  panelDir!: DropdownPanelDirective;

  private overlayRef?: OverlayRef;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef
  ) {}

  toggle(): void {
    if (this.overlayRef?.hasAttached()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (!this.triggerDir || !this.panelDir) {
      console.warn(
        'OverlayDropdown: dropdownTrigger or dropdownPanel is missing'
      );
      return;
    }

    const positions = this.mapPositions(this.position);

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
    .position()
    .flexibleConnectedTo(this.triggerDir.el)
    .withPositions(positions)
    .withFlexibleDimensions(true)
    .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      // backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new TemplatePortal(this.panelDir.template, this.vcr);
    this.overlayRef.attach(portal);

    this.overlayRef.backdropClick().subscribe(() => this.close());
  }

  close(): void {
    this.overlayRef?.detach();
  }

  private mapPositions(input: OverlayDropdownPosition[]): ConnectedPosition[] {
    const map: Record<OverlayDropdownPosition, ConnectedPosition> = {
      [OverlayDropdownPosition.BOTTOM_START]: {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 4,
      },
      [OverlayDropdownPosition.BOTTOM_END]: {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 4,
      },
      [OverlayDropdownPosition.TOP_START]: {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -4,
      },
      [OverlayDropdownPosition.TOP_END]: {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: -4,
      },
      [OverlayDropdownPosition.RIGHT_START]: {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetX: 4,
      },
      [OverlayDropdownPosition.RIGHT_END]: {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetX: 4,
      },
      [OverlayDropdownPosition.LEFT_START]: {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: -4,
      },
      [OverlayDropdownPosition.LEFT_END]: {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetX: -4,
      },
    };

    return input
    .map(position => map[position])
    .filter((p): p is ConnectedPosition => Boolean(p));
  }
}
